import './ShipmentForm.scss';

import { ExportOutlined } from '@ant-design/icons';
import { DateField } from '@refinedev/antd';
import { Alert, Card, Form, message, Modal } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { ADMIN_URL, COMPANY_URLS } from '@/constants/urls';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';

import ShipmentAddressForm from './ShipmentAddressForm';
import ShipmentDetailForm from './ShipmentDetailForm';
import ShipmentPaymentForm from './ShipmentPaymentForm';
import { useShipmentForm } from './useShipmentForm';

interface ShipmentFormProps {
  formProps: FormProps;
  edit?: boolean;
  saveAddress: boolean;
  setSaveAddress: (value: boolean) => void;
  packageItems: PackageItem[];
  setPackageItems: (items: PackageItem[]) => void;
}

const ShipmentForm = (props: ShipmentFormProps) => {
  const { t } = useTranslation();
  const { formProps, edit, setSaveAddress, packageItems, setPackageItems } = props;
  const [sp] = useSearchParams();
  const ticketId = sp.get('ticket') ? Number(sp.get('ticket')) : undefined;
  const { initialValues } = formProps;
  const initialValuesModified = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            company: initialValues?.company?.id,
            shipment_payment_company: initialValues?.shipment_payment_company?.id,
            shipper: initialValues?.shipper?.id,
            receiver_localidad: initialValues?.receiver_localidad?.id,
            receiver_provincia: initialValues?.receiver_localidad?.provincia?.id,
            receiver_pais: initialValues?.receiver_localidad?.provincia?.pais?.id,
            ticket: edit ? initialValues?.ticket : ticketId ? ticketId : initialValues?.ticket?.id,
          }
        : ticketId
          ? { ticket: ticketId, receiver_pais: 1 }
          : { receiver_pais: 1 },

    [edit, initialValues, ticketId],
  );
  const {
    companySelectProps,
    shippersSelectProps,
    ticketsSelectProps,
    localidadesSelectProps,
    paisesSelectProps,
    provinciasSelectProps,
    technicianSelectProps,
    initialValuesSetted,
    blockPreventive,
    movilLink,
  } = useShipmentForm(formProps, initialValuesModified, edit);

  return (
    <Form
      {...formProps}
      onFinish={values => {
        if (
          blockPreventive &&
          packageItems.some(
            item =>
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.PREVENTIVO ||
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE ||
              item.item_shipped_reference == SHIPPING_ITEM_REFERENCE.SIN_DEVOLUCION,
          )
        ) {
          Modal.error({
            title: t('Envío preventivo bloqueado'),
            content: t(
              'El envío contiene elementos preventivos, sujetos a peritaje o sin devolución y la empresa de facturación o la empresa destino tienen un bloqueo activo para este tipo de envíos',
            ),
            okText: t('Aceptar'),
            maskClosable: true,
          });

          return;
        }

        const companyWarn = formProps.form?.getFieldWarning('company') || [];
        const shipmentPaymentComapnyWarn =
          formProps.form?.getFieldWarning('shipment_payment_company') || [];

        if (companyWarn?.length > 0 || shipmentPaymentComapnyWarn?.length > 0) {
          Modal.error({
            title: t('Faltan datos de contacto'),
            content: (
              <div className="ShipmentFormWarnModal">
                {t('Faltan datos de contacto')}. {t('Por favor, revise')}
                <ul>
                  {companyWarn?.length > 0 && (
                    <li>
                      <a
                        className="ShipmentFormWarnLink"
                        href={`${ADMIN_URL}${COMPANY_URLS.ADMIN_EMPRESA}/${values.company}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        {t('Empresa o Técnico que recibirá el paquete')}{' '}
                        <ExportOutlined className="ShipmentFormWarnLinkIcon" />
                      </a>
                    </li>
                  )}
                  {shipmentPaymentComapnyWarn?.length > 0 && (
                    <li>
                      <a
                        className="ShipmentFormWarnLink"
                        href={`${ADMIN_URL}${COMPANY_URLS.ADMIN_EMPRESA}/${values.shipment_payment_company}`}
                        target="_blank"
                        rel="noopener noreferrer">
                        {t('Empresa para facturación')}{' '}
                        <ExportOutlined className="ShipmentFormWarnLinkIcon" />
                      </a>
                    </li>
                  )}
                </ul>
              </div>
            ),
            okText: t('Aceptar'),
            maskClosable: true,
          });

          return;
        } else {
          if (formProps?.onFinish) {
            if (values.technician === -1) {
              values.technician = null;
            }

            formProps.onFinish(values);
          }
        }
      }}
      layout="vertical"
      className="ShipmentForm"
      initialValues={initialValuesModified}
      onFinishFailed={errorInfo => {
        if (errorInfo.errorFields.length > 0) {
          message.error(t('Por favor, revisa los campos requeridos antes de enviar el formulario'));
        }
      }}>
      {edit && (
        <div className="ShipmentFormRow">
          <div>
            <div className="ShipmentFormDataId">
              {t('Id de envío')}: {initialValues?.id}
            </div>
            {initialValues?.status != undefined && (
              <PackageStateTag packageStatus={initialValues?.status} />
            )}
          </div>
          <div>
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={initialValues?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={initialValues?.updated_at} />
            </div>
          </div>
        </div>
      )}
      <Card className="ShipmentFormSection">
        <Form.Item
          label={<b>{t('Ticket')}</b>}
          name={'ticket'}
          rules={[
            {
              required: true,
            },
          ]}>
          <AntdSelect {...ticketsSelectProps} allowClear showSearch />
        </Form.Item>
      </Card>
      {blockPreventive && (
        <Alert
          style={{ padding: 16 }}
          type="error"
          showIcon
          message={
            <b>
              {t(
                'Este cliente esta sujeto a un bloqueo de envíos preventivos, sujetos a peritaje o sin devolución',
              )}
            </b>
          }
        />
      )}
      <Card className="ShipmentFormSection">
        <div className="ShipmentFormTitle">{t('Detalles de facturación')}</div>
        <ShipmentPaymentForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          edit={edit}
        />
      </Card>
      <Card className="ShipmentFormSection">
        <div className="ShipmentFormTitle">{t('Detalles del envío')}</div>
        <ShipmentDetailForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          shippersSelectProps={shippersSelectProps}
          ticketsSelectProps={ticketsSelectProps}
          edit={edit}
        />
      </Card>

      <Card className="ShipmentFormSection">
        <div className="ShipmentFormTitle">{t('Dirección de envío')}</div>
        <ShipmentAddressForm
          formProps={formProps}
          localidadesSelectProps={localidadesSelectProps}
          paisesSelectProps={paisesSelectProps}
          provinciasSelectProps={provinciasSelectProps}
          setSaveAddress={setSaveAddress}
          initialValuesSetted={initialValuesSetted}
        />
      </Card>
      <Card className="ShipmentFormSection">
        <Form.Item
          label={<b>{t('Técnico involucrado')}</b>}
          name={'technician'}
          rules={[{ required: true, message: t('Campo requerido') }]}>
          <AntdSelect
            {...technicianSelectProps}
            options={[
              { label: 'No habrá técnico involucrado', value: -1 },
              ...(technicianSelectProps.options ?? []),
            ]}
            allowClear
            showSearch
          />
        </Form.Item>
      </Card>
      <div className="ShipmentFormTitle">{t('Elementos')}</div>
      <PackageItemsList
        packageItems={packageItems}
        setPackageItems={setPackageItems}
        package={{ id: initialValues?.id, movil_link: movilLink }}
        editMode="enabled"
      />
    </Form>
  );
};

export default ShipmentForm;
